import { Box, Button, FormComponent, Text } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { stringToDate } from 'modules/core/lib/dates'
import { weekdaysWithUserFirstDayOfWeek } from 'modules/core/lib/firstDayOfWeek'
import { formatSecondsToHM } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import findDayConfigurationFormsWithConflicts from 'modules/work_schedules/hooks/findDayConfigurationFormsWithConflicts'
import { DayConfigurationWithBreaksForm } from 'modules/work_schedules/hooks/useDayConfigurations'
import useWeekdaysName from 'modules/work_schedules/hooks/useWeekdaysName'
import type DayConfigurationForm from 'modules/work_schedules/lib/forms/DayConfigurationForm'
import type WorkSchedulePeriodForm from 'modules/work_schedules/lib/forms/WorkSchedulePeriodForm'

import FixedDayConfigurationInput from './FixedDayConfigurationInput'
import NormalText from './NormalText'

export interface Props {
  onSubmit: () => void
  periodFormRef: WorkSchedulePeriodForm
  dayConfigForms: DayConfigurationWithBreaksForm[]
  totalHours: number
  onAddDayConfig: (weekday: string) => void
  onDeleteDayConfig: (dayConfigForm: DayConfigurationForm) => void
  onCopyFirstToAll: () => void
  onAddBreak: (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    dayConfiguration: DayConfigurationWithBreaksForm
  ) => void
  onDeleteBreak: (dayConfiguration: DayConfigurationWithBreaksForm, form: PlannedBreakForm) => void
  disabled?: boolean
  edit?: boolean
  submitText?: string
  canAddBreaks: boolean
}

const FixedPeriodForm = ({
  onSubmit,
  periodFormRef,
  dayConfigForms,
  totalHours,
  onAddDayConfig,
  onDeleteDayConfig,
  onCopyFirstToAll,
  onAddBreak,
  onDeleteBreak,
  disabled,
  edit,
  submitText = i18n.t('settings.work_schedule.period_section.submit'),
  canAddBreaks,
}: Props) => {
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()
  const { shortWeekDaysName } = useWeekdaysName()

  const filteredDayConfigs = (weekday: string) => {
    return dayConfigForms.filter(
      (dayConfig) => dayConfig.dayConfiguration.get('weekday').value === weekday
    )
  }

  const isAllEmpty = dayConfigForms.every(({ dayConfiguration: { isEmpty } }) => isEmpty)

  const isFixedPeriodFormValid = dayConfigForms.every(({ dayConfiguration, breaks }) => {
    const { isValid, isClean, formattedStartAt: startAt, formattedEndAt: endAt } = dayConfiguration

    return (
      (isValid || isClean) &&
      breaks.every((form) => {
        return form.isValid({ startAt, endAt })
      })
    )
  })

  const startDateValue = periodFormRef.get('start_date').value
  const endDateValue = periodFormRef.get('end_date').value

  const dayConfigurationsWithConflicts = findDayConfigurationFormsWithConflicts(
    dayConfigForms.map((form) => form.dayConfiguration)
  )
  const hasOverlappedDayConfigurations = !!dayConfigurationsWithConflicts.length

  const showErrorNotification = (dayConfigForm: DayConfigurationForm) => {
    const hasConflict = dayConfigurationsWithConflicts.some((dc) => dc === dayConfigForm)

    if (!hasConflict) return

    app.setFlash({
      type: 'wrong',
      message: i18n.t('settings.work_schedule.period_section.errors.day_configuration_overlap', {
        from_date: dayConfigForm.get('start_at').value,
        to_date: dayConfigForm.get('end_at').value,
      }),
    })
  }

  const setErrors = () => {
    dayConfigurationsWithConflicts.forEach((dayConfig) => {
      // Empty errors to show only red borders without message
      dayConfig.get('start_at').setErrors([' '])
      dayConfig.get('end_at').setErrors([' '])
    })
  }

  const onBlurAction = (dayConfigForm: DayConfigurationForm) => {
    dayConfigForms.forEach((dayConfig) => dayConfig.dayConfiguration.resetErrors())

    if (hasOverlappedDayConfigurations) {
      showErrorNotification(dayConfigForm)
      setErrors()
    }
  }

  useUnsafeEffect(() => {
    dayConfigForms.forEach((dayConfig) => dayConfig.dayConfiguration.resetErrors())

    if (hasOverlappedDayConfigurations) {
      setErrors()
    }
  }, [dayConfigForms]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Box gap="s32">
        <Box flexDirection="row" gap="s16">
          <DayPicker
            hideYear
            field={periodFormRef.get('start_date')}
            label={i18n.t('settings.work_schedule.settings_tab.starting_from')}
            readonly={!edit}
            value={!edit ? startDateValue : ''}
            format="ui_dm_long"
          />

          <DayPicker
            hideYear
            field={periodFormRef.get('end_date')}
            label={i18n.t('common.ending_at')}
            minDate={stringToDate(periodFormRef.get('start_date').value, 'ui')}
            readonly={!edit}
            value={!edit ? endDateValue : ''}
            format="ui_dm_long"
          />
        </Box>
        <Box gap="s16">
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
            <Text.Normal color="grey800" size="200" weight="medium">
              {i18n.t('settings.work_schedule.period_section.working_hours')}
            </Text.Normal>
            <Button onClick={onCopyFirstToAll} type="secondary" iconName="layers-front">
              {i18n.t('shift_management.templates.weeks.settings_copy')}
            </Button>
          </Box>
          <Box>
            {weekdaysWithUserFirstDayOfWeek(firstDayOfWeek).map((weekday: string, idx: number) => {
              return (
                <Box paddingBottom="s16" key={idx}>
                  <FixedDayConfigurationInput
                    key={idx}
                    day={shortWeekDaysName[weekday] ?? ''}
                    weekday={weekday}
                    dayConfigForms={filteredDayConfigs(weekday)}
                    onAddDayConfig={onAddDayConfig}
                    onDeleteDayConfig={onDeleteDayConfig}
                    onBlur={onBlurAction}
                    onAddBreak={onAddBreak}
                    onDeleteBreak={onDeleteBreak}
                    canAddBreaks={canAddBreaks}
                  />
                </Box>
              )
            })}
          </Box>
        </Box>
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          paddingX="s24"
          paddingY="s12"
          background={edit ? 'grey100' : 'grey000White'}
          borderRadius={{
            all: 'abs008',
          }}
        >
          <NormalText text={i18n.t('settings.work_schedule.period_section.total')} />
          <NormalText text={formatSecondsToHM(totalHours)} />
        </Box>
      </Box>

      <FormComponent.Buttons>
        <Button
          onClick={onSubmit}
          disabled={
            disabled || hasOverlappedDayConfigurations || !isFixedPeriodFormValid || isAllEmpty
          }
        >
          {submitText}
        </Button>
      </FormComponent.Buttons>
    </>
  )
}

export default observer(FixedPeriodForm)
